import { useTranslation } from 'react-i18next';
import { useUserRoles } from '@anytag/auth';
import { ColumnDef } from '@tanstack/react-table';
import { TableCell, TableV2 } from '@anytag/shared-atoms';
import { getOffset, formatPrice, formatPercent, formatIntNumber, getValuableVariables } from '@anytag/shared-utils';
import { AutoManagedReportByDate, AutoManagedReportSummary, useAutoManagedReportByDateQuery } from '@anytag/graphql';
import { useAutoManagedReportFilter } from '../hooks';
import { formatAffiliatePeriod } from './utils';

interface AutoManagedReportByDateRowData {
  className?: string;
  date?: AutoManagedReportByDate['date'];
  stats?: Omit<AutoManagedReportByDate, 'date'>;
}

interface AutoManagedReportByDateTableProps {
  currency: string;
  campaignId: number;
  summaryData?: AutoManagedReportSummary;
}

export const AutoManagedReportByDateTable = ({
  currency,
  campaignId,
  summaryData
}: AutoManagedReportByDateTableProps) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const {
    page,
    limit,
    filter: { endDate, startDate }
  } = useAutoManagedReportFilter();
  const { data, loading } = useAutoManagedReportByDateQuery({
    variables: getValuableVariables({
      limit,
      endDate,
      startDate,
      id: campaignId,
      offset: getOffset(page, limit)
    })
  });

  const columns: ColumnDef<AutoManagedReportByDateRowData>[] = [
    {
      accessorKey: 'date',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Date'),
      cell: ({ row }) => {
        const { className, date } = row.original;

        return className === 'total-row' ? (
          <TableCell.CampaignTotal
            title={
              <TableCell.Text
                value={formatAffiliatePeriod({
                  title: t('Total'),
                  period: { endDate: endDate || '', startDate: startDate || '' }
                })}
              />
            }
          />
        ) : (
          <TableCell.Date value={date} />
        );
      }
    },
    {
      accessorKey: 'clicks',
      header: t<string>('HeaderColumn.Click'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.clicks)} />
    },
    {
      accessorKey: 'conversions',
      header: t<string>('HeaderColumn.Order'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.conversions)} />
    },
    {
      accessorKey: 'cvr',
      header: t<string>('HeaderColumn.CVR'),
      meta: { align: 'right' },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.cvr, false)} unit="%" />
    },
    {
      accessorKey: 'sales',
      header: t<string>('HeaderColumn.Store Sales'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.sales || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'cost',
      header: t<string>('HeaderColumn.Cost'),
      meta: { align: 'right' },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.cost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'influencerCost',
      header: t<string>('HeaderColumn.Influencer Cost'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.influencerCost || null, currency)} unit={currency} />
      )
    },
    {
      accessorKey: 'grossProfit',
      header: t<string>('HeaderColumn.Gross Profit'),
      meta: { align: 'right', hidden: !isAdminStaff },
      cell: ({ row }) => (
        <TableCell.Number value={formatPrice(row.original.stats?.grossProfit || null, currency)} unit={currency} />
      )
    }
  ];

  const tableData: AutoManagedReportByDateRowData[] = [
    { className: 'total-row', stats: summaryData },
    ...(data?.autoManagedReportByDate?.map<AutoManagedReportByDateRowData>(({ date, ...stats }) => ({ date, stats })) ||
      [])
  ];

  return (
    <TableV2
      loading={loading}
      data={tableData}
      columns={columns}
      rowHeight="73px"
      className="report-table"
      pagination={{ totalRecords: data?.autoManagedReportByDateCount?.totalNumber }}
      noTopBorderRadius
      stickyHeader
    />
  );
};
